// ApplicationSubmissionModal.jsx
import React, { useState, useCallback } from 'react'
import EligibilityAndRuleError from '../components/EligibilityAndRuleError';

const BENEFIT_SECTION_LABELS = {
  feeBenefits: "Fee Benefits",
  additionalBenefits: "Additional Benefits",
  totalAmount: "Total Amount",
  installments: "Installments",
};

/** Standard keys shown first; dynamic Drools labels follow (sorted). */
const FEE_BENEFIT_STANDARD_ORDER = [
  "tuitionFeeCoveragePercentage",
  "examFeeCoveragePercentage",
  "admissionFeeCoveragePercentage",
  "semesterFeeCoveragePercentage",
  "libraryFeeCoveragePercentage",
  "laboratoryFeeCoveragePercentage",
  "gymkhanaFeeCoveragePercentage",
  "tuitionBenefit",
  "examBenefit",
];

/** Single summary row under Additional Benefits (API root `allowance`). */
const ADDITIONAL_ALLOWANCE_DISPLAY_KEY = "allowance";

/**
 * Drools sometimes emits these as separate line items; we hide them (totals roll into Allowance).
 */
function shouldHideFeeBenefitLineKey(key) {
  const normalized = String(key || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return (
    normalized === "meal allowance" ||
    normalized === "residence allowance" ||
    normalized === "subsistence allowance"
  );
}

const INSTALLMENT_ROW_ORDER = ["Installment 1", "Installment 2"];

const BENEFITS_MODAL_HEADER_BG = "#0f3b82";
const BENEFITS_SECTION_HEADER_BG = "#e8e4f3";

function formatBenefitLabel(key) {
  const raw = String(key || "").trim();
  if (!raw) return "";
  if (/\s/.test(raw)) {
    return raw.replace(/\b\w/g, (char) => char.toUpperCase());
  }
  return raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatBenefitNumber(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return value ?? "N/A";
  }

  return numericValue.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatBenefitValue(key, value) {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (String(key).toLowerCase().includes("percentage")) {
    return `${formatBenefitNumber(value)}%`;
  }

  if (!Number.isNaN(Number(value))) {
    return `Rs. ${formatBenefitNumber(value)}`;
  }

  return value;
}

function isZeroBenefitNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n === 0;
}

function buildFeeBenefitDisplayRows(feeBenefits = {}) {
  const seen = new Set();
  const out = [];
  for (const key of FEE_BENEFIT_STANDARD_ORDER) {
    if (!Object.prototype.hasOwnProperty.call(feeBenefits, key)) continue;
    seen.add(key);
    const v = feeBenefits[key];
    if (isZeroBenefitNumber(v)) continue;
    out.push([key, v]);
  }
  const dynamicKeys = Object.keys(feeBenefits)
    .filter((k) => !seen.has(k) && !shouldHideFeeBenefitLineKey(k))
    .sort((a, b) => String(a).localeCompare(String(b)));
  for (const key of dynamicKeys) {
    const v = feeBenefits[key];
    if (isZeroBenefitNumber(v)) continue;
    out.push([key, v]);
  }
  return out;
}

/** Always one row: Allowance (show Rs. 0.00 when zero or missing). */
function buildAdditionalBenefitDisplayRows(additionalBenefits = {}) {
  const raw = additionalBenefits?.[ADDITIONAL_ALLOWANCE_DISPLAY_KEY];
  let value = 0;
  if (raw !== null && raw !== undefined && raw !== "") {
    const n = Number(raw);
    value = Number.isFinite(n) ? n : 0;
  }
  return [[ADDITIONAL_ALLOWANCE_DISPLAY_KEY, value]];
}

function getOrderedInstallmentRows(installments = {}) {
  const merged = {
    "Installment 1": 0,
    "Installment 2": 0,
    ...installments,
  };
  const rows = [];
  const seen = new Set();
  for (const key of INSTALLMENT_ROW_ORDER) {
    if (Object.prototype.hasOwnProperty.call(merged, key)) {
      rows.push([key, merged[key]]);
      seen.add(key);
    }
  }
  const rest = Object.keys(merged)
    .filter((k) => !seen.has(k))
    .sort((a, b) => String(a).localeCompare(String(b)));
  for (const key of rest) {
    rows.push([key, merged[key]]);
  }
  return rows.filter(([, v]) => !isZeroBenefitNumber(v));
}

function ApplicationSubmissionModal({submissionModal,setSubmissionModal,scrollToFirstErrorField,navigate}) {
  const [showEligibilityErrorModal, setShowEligibilityErrorModal] = useState(false);
  const hasBenefitsData =
    submissionModal?.benefitsData &&
    Object.keys(submissionModal.benefitsData).length > 0;
  const isBenefitsModal =
    String(submissionModal?.title || "").trim().toLowerCase() === "benefits details";
  const feeBenefitRows = hasBenefitsData
    ? buildFeeBenefitDisplayRows(submissionModal.benefitsData.feeBenefits || {})
    : [];
  const additionalBenefitRows = hasBenefitsData
    ? buildAdditionalBenefitDisplayRows(
        submissionModal.benefitsData.additionalBenefits || {},
      )
    : [];
  const totalAmountRow = hasBenefitsData
    ? submissionModal.benefitsData.totalAmount
    : null;
  const installmentRows = hasBenefitsData
    ? getOrderedInstallmentRows(submissionModal.benefitsData.installments || {})
    : [];
  const isPlainMessage = typeof submissionModal.message === "string";

  // Memoize the close function to prevent unnecessary re-renders
  const handleCloseEligibilityError = useCallback(() => {
    setShowEligibilityErrorModal(false);
  }, []);

  const handleViewErrors = useCallback(() => {
    setShowEligibilityErrorModal(true);
  }, []);

  const handleCloseMainModal = useCallback(() => {
    setSubmissionModal(m => ({ ...m, show: false }));
  }, [setSubmissionModal]);

  // Stop propagation when clicking inside modals
  const handleModalClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <>
      {/* Main Submission Modal - Only show if submissionModal.show is true */}
      {submissionModal.show && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
          <div 
            className="modal fade show d-block" 
            tabIndex="-1" 
            role="dialog" 
            aria-modal="true" 
            style={{ zIndex: 1041 }}
            onClick={handleModalClick}
          >
            <div
              className={`modal-dialog modal-dialog-centered modal-dialog-scrollable ${
                isBenefitsModal || hasBenefitsData || submissionModal?.benefitsError
                  ? "modal-lg"
                  : ""
              }`}
            >
              <div className="modal-content overflow-hidden">
                <div
                  className={`modal-header text-white border-0 ${
                    isBenefitsModal
                      ? ""
                      : submissionModal.variant === "info"
                        ? "bg-info"
                        : submissionModal.variant === "primary"
                          ? "bg-primary"
                          : submissionModal.variant === "danger"
                            ? "bg-danger"
                            : submissionModal.variant === "warning"
                              ? "bg-warning"
                              : "bg-secondary"
                  }`}
                  style={
                    isBenefitsModal
                      ? { backgroundColor: BENEFITS_MODAL_HEADER_BG }
                      : undefined
                  }
                >
                  <h5 className="modal-title d-flex align-items-center mb-0">
                    {submissionModal.variant === "info" && (
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    )}
                    {submissionModal.title}
                  </h5>
                  {submissionModal.variant !== "info" &&
                    submissionModal.variant !== "danger" &&
                    submissionModal.variant !== "warning" && (
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      aria-label="Close"
                      onClick={handleCloseMainModal}
                    />
                  )}
                </div>

                <div className="modal-body">
                  {submissionModal.steps && (
                    <div className="mb-4">
                      <div className="d-flex justify-content-between mb-3 position-relative">
                        {submissionModal.steps.map((step, index) => (
                          <React.Fragment key={step.id}>
                            <div className="d-flex flex-column align-items-center" style={{ zIndex: 1, width: `${100 / submissionModal.steps.length}%` }}>
                              <div className={`
                                rounded-circle d-flex align-items-center justify-content-center mb-2
                                ${step.status === 'completed' ? 'bg-primary text-white' :
                                  step.status === 'processing' ? 'bg-info text-white' :
                                    step.status === 'error' ? 'bg-danger text-white' :
                                      step.status === 'skipped' ? 'bg-secondary text-white' :
                                        'bg-light text-secondary border'}
                              `} style={{ width: '40px', height: '40px' }}>
                                {step.status === 'completed' ? '✓' :
                                  step.status === 'processing' ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) :
                                    step.status === 'error' ? '✗' :
                                      step.status === 'skipped' ? '–' : step.id}
                              </div>

                              <div className="text-center" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                                <div className="fw-medium">{step.name}</div>
                                {step.result && step.result !== 'N/A' && (
                                  <div className={`badge ${step.result === 'Success' ? 'bg-primary' :
                                    step.result === 'Passed' ? 'bg-primary' :
                                      step.result === 'Eligible' ? 'bg-primary' :
                                        step.result === 'Completed' ? 'bg-primary' :
                                          step.result === 'Applied' ? 'bg-warning' :
                                            step.result === 'Failed' ? 'bg-danger' :
                                              step.result === 'Not Eligible' ? 'bg-danger' :
                                                step.result === 'N/A' ? 'bg-secondary' :
                                                  'bg-info'
                                    } mt-1`}>
                                    {step.result}
                                  </div>
                                )}
                              </div>
                            </div>

                            {index < submissionModal.steps.length - 1 && (
                              <div className="position-absolute" style={{
                                left: `${(100 / (submissionModal.steps.length * 2)) + (index * (100 / submissionModal.steps.length))}%`,
                                top: '20px',
                                width: `${100 / submissionModal.steps.length}%`,
                                height: '2px',
                                backgroundColor: step.status === 'completed' ? '#0d6efd' :
                                  step.status === 'skipped' ? '#6c757d' : '#dee2e6',
                                zIndex: 0
                              }}></div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}

                  {isPlainMessage ? (
                    <div className="alert alert-light border mb-0">
                      <p className="mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                        {submissionModal.message}
                      </p>
                    </div>
                  ) : (
                    submissionModal.message
                  )}

                  {submissionModal?.benefitsError && (
                    <div className="alert alert-warning mt-3 mb-0">
                      {submissionModal.benefitsError}
                      {Array.isArray(submissionModal?.benefitsErrorList) &&
                        submissionModal.benefitsErrorList.length > 0 ? (
                        <ul className="mb-0 mt-2 ps-4">
                          {submissionModal.benefitsErrorList.map((fieldName) => (
                            <li key={fieldName}>{fieldName}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  )}

                  {!submissionModal?.benefitsError &&
                    String(submissionModal?.benefitHtml || "").trim() ? (
                    <div
                      className="table-responsive mt-3"
                      dangerouslySetInnerHTML={{
                        __html: String(submissionModal.benefitHtml || ""),
                      }}
                    />
                  ) : null}

                  {hasBenefitsData && (
                    <div className="mt-3">
                      <h6 className="mb-3 fw-semibold">Benefit Calculation Summary</h6>
                      <div className="table-responsive rounded border" style={{ borderColor: "#d9deea" }}>
                        <table className="table table-bordered table-striped align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">Particular</th>
                              <th scope="col" className="text-end">
                                Value
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {feeBenefitRows.length > 0 ? (
                              <>
                                <tr style={{ backgroundColor: BENEFITS_SECTION_HEADER_BG }}>
                                  <th colSpan="2" className="border py-2 fw-bold">
                                    {BENEFIT_SECTION_LABELS.feeBenefits}
                                  </th>
                                </tr>
                                {feeBenefitRows.map(([key, value]) => (
                                  <tr key={key}>
                                    <td>{formatBenefitLabel(key)}</td>
                                    <td className="text-end">{formatBenefitValue(key, value)}</td>
                                  </tr>
                                ))}
                              </>
                            ) : null}

                            {additionalBenefitRows.length > 0 ? (
                              <>
                                <tr style={{ backgroundColor: BENEFITS_SECTION_HEADER_BG }}>
                                  <th colSpan="2" className="border py-2 fw-bold">
                                    {BENEFIT_SECTION_LABELS.additionalBenefits}
                                  </th>
                                </tr>
                                {additionalBenefitRows.map(([key, value]) => (
                                  <tr key={key}>
                                    <td>{formatBenefitLabel(key)}</td>
                                    <td className="text-end">{formatBenefitValue(key, value)}</td>
                                  </tr>
                                ))}
                              </>
                            ) : null}

                            <tr style={{ backgroundColor: BENEFITS_SECTION_HEADER_BG }}>
                              <th colSpan="2" className="border py-2 fw-bold">
                                {BENEFIT_SECTION_LABELS.totalAmount}
                              </th>
                            </tr>
                            <tr>
                              <td>{BENEFIT_SECTION_LABELS.totalAmount}</td>
                              <td className="text-end">
                                {formatBenefitValue("totalAmount", totalAmountRow ?? 0)}
                              </td>
                            </tr>

                            {installmentRows.length > 0 ? (
                              <>
                                <tr style={{ backgroundColor: BENEFITS_SECTION_HEADER_BG }}>
                                  <th colSpan="2" className="border py-2 fw-bold">
                                    {BENEFIT_SECTION_LABELS.installments}
                                  </th>
                                </tr>
                                {installmentRows.map(([label, value]) => (
                                  <tr key={String(label)}>
                                    <td>{label}</td>
                                    <td className="text-end">{formatBenefitValue(label, value)}</td>
                                  </tr>
                                ))}
                              </>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  {submissionModal.variant === 'info' ? (
                    <div className="w-100 text-center">
                      <div className="spinner-border text-primary me-2" role="status"></div>
                      <span className="text-muted">Processing...</span>
                    </div>
                  ) 
                   : submissionModal.variant === 'primary' && submissionModal?.isBlocked ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleCloseMainModal}
                    >
                      Close
                    </button>

                  )
                   : submissionModal.variant === 'primary' && submissionModal?.isVerified ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleCloseMainModal}
                    >
                      OK
                    </button>
                    
                  )
                   : submissionModal.variant === 'primary' && submissionModal?.isOtr ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleCloseMainModal}
                    >
                      OK
                    </button>
                    
                  )
                   : submissionModal.variant === 'primary' && submissionModal?.flag ? (
                    <div className="d-flex justify-content-between w-100">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCloseMainModal}
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          handleCloseMainModal();
                          scrollToFirstErrorField();
                        }}
                      >
                        Go to Errors
                      </button>
                    </div>
                  ) 
                   : submissionModal.variant === 'primary' ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        handleCloseMainModal();
                        navigate('/');
                      }}
                    >
                      Done
                    </button>
                    
                  ):
                   submissionModal.variant === 'danger' && submissionModal?.errors ? (
                    <div className="d-flex justify-content-between w-100">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleViewErrors}
                        disabled={showEligibilityErrorModal}
                      >
                        View Errors
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleCloseMainModal}
                        disabled={showEligibilityErrorModal}
                      >
                       Close
                      </button>
                    </div>
                    
                  ):
                  (
                    <button
                      type="button"
                      className={
                        hasBenefitsData
                          ? "btn text-white"
                          : `btn ${submissionModal.variant === "danger" ? "btn-danger" : submissionModal.variant === "warning" ? "btn-warning" : "btn-secondary"}`
                      }
                      style={
                        hasBenefitsData
                          ? {
                              backgroundColor: BENEFITS_MODAL_HEADER_BG,
                              borderColor: BENEFITS_MODAL_HEADER_BG,
                              minWidth: "100px",
                            }
                          : undefined
                      }
                      onClick={handleCloseMainModal}
                    >
                      Close
                    </button>
                  )
                  }
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Rule Error Modal - Shows error details ON TOP */}
      {showEligibilityErrorModal && (
        <EligibilityAndRuleError 
          eligibilityRules={submissionModal.eligibilityRules} 
          errors={submissionModal.errors}
          onClose={handleCloseEligibilityError}
        />
      )}
    </>
  )
}

export default ApplicationSubmissionModal
